<template>
  <div id="settings-manager" class="settings-container">
    <!-- Header com botão de voltar -->
    <div class="settings-header">
      <v-btn
        icon
        small
        @click="$router.back()"
        class="back-button"
      >
        <v-icon color="grey darken-3">mdi-arrow-left</v-icon>
      </v-btn>
    </div>

    <!-- Card centralizado -->
    <v-card
      id="settings-card"
      class="settings-card"
      elevation="0"
      outlined
    >
      <v-card-title class="justify-center pt-8 pb-4">
        <div class="text-center">
          <v-avatar color="indigo lighten-4" size="56" class="mb-4">
            <v-icon color="indigo darken-2" size="32">mdi-account-cog</v-icon>
          </v-avatar>
          <div class="text-h5 font-weight-bold mt-4">Configurações de Gestor</div>
          <div class="text-caption grey--text mt-1">Configurar preferências de gestão de equipe</div>
        </div>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Aviso de desenvolvimento -->
        <v-alert
          type="warning"
          text
          icon="mdi-alert"
          class="mb-4"
        >
          <strong>Em Desenvolvimento:</strong> Esta funcionalidade ainda não está conectada ao backend.
        </v-alert>

        <!-- Tabs -->
        <v-tabs v-model="tab" color="indigo" class="mb-4">
          <v-tab>
            <v-icon left small>mdi-account-group</v-icon>
            <span class="d-none d-sm-inline">Equipe</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-bell-outline</v-icon>
            <span class="d-none d-sm-inline">Notificações</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-calendar-check</v-icon>
            <span class="d-none d-sm-inline">Metas</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-chart-line</v-icon>
            <span class="d-none d-sm-inline">Relatórios</span>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!--aba Equipe -->
          <v-tab-item>
            <div class="tab-content">
              <h3 class="text-h6 mb-4">Configurações da Equipe</h3>
              
              <v-switch
                v-model="teamSettings.autoApproveTimeOff"
                label="Aprovar automaticamente solicitações de folga"
                color="indigo"
                hint="Folgas de até 1 dia serão aprovadas automaticamente"
                persistent-hint
                class="mb-4"
              />

              <v-switch
                v-model="teamSettings.allowSelfEvaluation"
                label="Permitir autoavaliação"
                color="indigo"
                hint="Membros da equipe podem se autoavaliar"
                persistent-hint
                class="mb-4"
              />

              <v-select
                v-model="teamSettings.oneOnOneFrequency"
                :items="frequencyOptions"
                label="Frequência de One-on-One"
                outlined
                dense
                hint="Frequência sugerida para reuniões 1:1"
                persistent-hint
                class="mb-4"
              />

              <v-select
                v-model="teamSettings.feedbackFrequency"
                :items="frequencyOptions"
                label="Frequência de Feedback"
                outlined
                dense
                hint="Frequência sugerida para feedbacks"
                persistent-hint
                class="mb-4"
              />

              <v-textarea
                v-model="teamSettings.teamGoals"
                label="Objetivos da Equipe"
                outlined
                rows="3"
                hint="Descreva os principais objetivos do seu time"
                persistent-hint
              />
            </div>
          </v-tab-item>

          <!-- Aba Notificações -->
          <v-tab-item>
            <div class="tab-content">
              <h3 class="text-h6 mb-4">Preferências de Notificações</h3>
              
              <p class="text-subtitle-2 mb-3">Notificar quando:</p>

              <v-switch
                v-model="notificationSettings.newTeamMember"
                label="Novo membro na equipe"
                color="indigo"
                dense
                class="mb-1"
              />

              <v-switch
                v-model="notificationSettings.teamMemberLeave"
                label="Membro sair da equipe"
                color="indigo"
                dense
                class="mb-1"
              />

              <v-switch
                v-model="notificationSettings.pdiSubmitted"
                label="PDI submetido para aprovação"
                color="indigo"
                dense
                class="mb-1"
              />

              <v-switch
                v-model="notificationSettings.oneOnOneScheduled"
                label="One-on-One agendado"
                color="indigo"
                dense
                class="mb-1"
              />

              <v-switch
                v-model="notificationSettings.feedbackReceived"
                label="Feedback recebido de membro da equipe"
                color="indigo"
                dense
                class="mb-1"
              />

              <v-switch
                v-model="notificationSettings.goalDeadline"
                label="Prazo de meta se aproximando"
                color="indigo"
                dense
                class="mb-4"
              />

              <p class="text-subtitle-2 mb-3">Canal de notificação:</p>

              <v-checkbox
                v-model="notificationSettings.channels"
                label="Email"
                value="email"
                color="indigo"
                dense
                class="mb-0"
              />

              <v-checkbox
                v-model="notificationSettings.channels"
                label="Push (App)"
                value="push"
                color="indigo"
                dense
                class="mb-0"
              />

              <v-checkbox
                v-model="notificationSettings.channels"
                label="SMS"
                value="sms"
                color="indigo"
                dense
              />
            </div>
          </v-tab-item>

          <!-- Aba Metas e Objetivos -->
          <v-tab-item>
            <div class="tab-content">
              <h3 class="text-h6 mb-4">Configurações de Metas e Objetivos</h3>
              
              <v-switch
                v-model="goalsSettings.cascadeGoals"
                label="Cascata de objetivos"
                color="indigo"
                hint="Objetivos da equipe derivam dos objetivos da empresa"
                persistent-hint
                class="mb-4"
              />

              <v-switch
                v-model="goalsSettings.requireAlignment"
                label="Exigir alinhamento com objetivos da equipe"
                color="indigo"
                hint="PDIs devem estar alinhados com objetivos do time"
                persistent-hint
                class="mb-4"
              />

              <v-select
                v-model="goalsSettings.reviewCycle"
                :items="reviewCycleOptions"
                label="Ciclo de Revisão de Metas"
                outlined
                dense
                class="mb-4"
              />

              <v-text-field
                v-model.number="goalsSettings.minGoalsPerPerson"
                label="Mínimo de metas por pessoa"
                type="number"
                outlined
                dense
                hint="Número mínimo de metas ativas por colaborador"
                persistent-hint
              />
            </div>
          </v-tab-item>

          <!-- Aba Relatórios -->
          <v-tab-item>
            <div class="tab-content">
              <h3 class="text-h6 mb-4">Configurações de Relatórios</h3>
              
              <v-select
                v-model="reportSettings.frequency"
                :items="reportFrequencyOptions"
                label="Frequência de Relatórios Automáticos"
                outlined
                dense
                class="mb-4"
              />

              <v-select
                v-model="reportSettings.dayOfWeek"
                :items="daysOfWeek"
                label="Dia da Semana"
                outlined
                dense
                :disabled="reportSettings.frequency !== 'WEEKLY'"
                class="mb-4"
              />

              <p class="text-subtitle-2 mb-3">Incluir nos relatórios:</p>

              <v-checkbox
                v-model="reportSettings.includeMetrics"
                label="Métricas de performance"
                value="performance"
                color="indigo"
                dense
                class="mb-0"
              />

              <v-checkbox
                v-model="reportSettings.includeMetrics"
                label="One-on-Ones realizados"
                value="oneOnOnes"
                color="indigo"
                dense
                class="mb-0"
              />

              <v-checkbox
                v-model="reportSettings.includeMetrics"
                label="Feedbacks dados/recebidos"
                value="feedbacks"
                color="indigo"
                dense
                class="mb-0"
              />

              <v-checkbox
                v-model="reportSettings.includeMetrics"
                label="PDIs em andamento"
                value="pdis"
                color="indigo"
                dense
                class="mb-0"
              />

              <v-checkbox
                v-model="reportSettings.includeMetrics"
                label="Campanhas ativas"
                value="campaigns"
                color="indigo"
                dense
                class="mb-0"
              />

              <v-checkbox
                v-model="reportSettings.includeMetrics"
                label="Evolução de competências"
                value="skills"
                color="indigo"
                dense
              />
            </div>
          </v-tab-item>
        </v-tabs-items>

        <!-- Botões de ação centralizado -->
        <div class="button-container mt-4">
          <v-btn
            color="indigo"
            rounded
            @click="saveSettings"
            class="action-button mr-2"
          >
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
          <v-btn
            outlined
            rounded
            @click="resetSettings"
            class="action-button"
          >
            <v-icon left>mdi-restore</v-icon>
            Restaurar
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Snackbar de feedback -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'SettingsManager',

  data: () => ({
    tab: 0,
    teamSettings: {
      autoApproveTimeOff: false,
      allowSelfEvaluation: true,
      oneOnOneFrequency: 'MONTHLY',
      feedbackFrequency: 'WEEKLY',
      teamGoals: '',
    },
    notificationSettings: {
      newTeamMember: true,
      teamMemberLeave: true,
      pdiSubmitted: true,
      oneOnOneScheduled: true,
      feedbackReceived: true,
      goalDeadline: true,
      channels: ['email', 'push'],
    },
    goalsSettings: {
      cascadeGoals: true,
      requireAlignment: true,
      reviewCycle: 'QUARTERLY',
      minGoalsPerPerson: 3,
    },
    reportSettings: {
      frequency: 'WEEKLY',
      dayOfWeek: 'MONDAY',
      includeMetrics: ['performance', 'oneOnOnes', 'feedbacks'],
    },
    frequencyOptions: [
      { text: 'Semanal', value: 'WEEKLY' },
      { text: 'Quinzenal', value: 'BIWEEKLY' },
      { text: 'Mensal', value: 'MONTHLY' },
      { text: 'Trimestral', value: 'QUARTERLY' },
    ],
    reviewCycleOptions: [
      { text: 'Mensal', value: 'MONTHLY' },
      { text: 'Trimestral', value: 'QUARTERLY' },
      { text: 'Semestral', value: 'BIANNUAL' },
      { text: 'Anual', value: 'ANNUAL' },
    ],
    reportFrequencyOptions: [
      { text: 'Diário', value: 'DAILY' },
      { text: 'Semanal', value: 'WEEKLY' },
      { text: 'Mensal', value: 'MONTHLY' },
      { text: 'Nunca', value: 'NEVER' },
    ],
    daysOfWeek: [
      { text: 'Segunda-feira', value: 'MONDAY' },
      { text: 'Terça-feira', value: 'TUESDAY' },
      { text: 'Quarta-feira', value: 'WEDNESDAY' },
      { text: 'Quinta-feira', value: 'THURSDAY' },
      { text: 'Sexta-feira', value: 'FRIDAY' },
    ],
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  methods: {
    async saveSettings () {
      console.log('💾 Salvar Configurações de Gestor:')
      console.log('  Equipe:', this.teamSettings)
      console.log('  Notificações:', this.notificationSettings)
      console.log('  Metas:', this.goalsSettings)
      console.log('  Relatórios:', this.reportSettings)

      // Simulação de chamada ao backend (mock)
      await new Promise(resolve => setTimeout(resolve, 1000))

      this.snackbarText = '✅ Configurações salvas com sucesso! (mock)'
      this.snackbarColor = 'success'
      this.snackbar = true
    },

    resetSettings () {
      this.teamSettings = {
        autoApproveTimeOff: false,
        allowSelfEvaluation: true,
        oneOnOneFrequency: 'MONTHLY',
        feedbackFrequency: 'WEEKLY',
        teamGoals: '',
      }
      this.notificationSettings = {
        newTeamMember: true,
        teamMemberLeave: true,
        pdiSubmitted: true,
        oneOnOneScheduled: true,
        feedbackReceived: true,
        goalDeadline: true,
        channels: ['email', 'push'],
      }
      this.goalsSettings = {
        cascadeGoals: true,
        requireAlignment: true,
        reviewCycle: 'QUARTERLY',
        minGoalsPerPerson: 3,
      }
      this.reportSettings = {
        frequency: 'WEEKLY',
        dayOfWeek: 'MONDAY',
        includeMetrics: ['performance', 'oneOnOnes', 'feedbacks'],
      }

      this.snackbarText = '🔄 Configurações restauradas para o padrão'
      this.snackbarColor = 'info'
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
/* Container principal - estilo macOS */
#settings-manager {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #F5F5F7 0%, #f0f2f5 100%);
}

.theme--dark #settings-manager {
  background: linear-gradient(135deg, #121212 0%, #1C1C1E 100%);
}

/* Header com botão de voltar */
.settings-header {
  position: fixed;
  top: 1rem;
  left: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
}

.back-button {
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.theme--dark .back-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Card centralizado - estilo macOS */
.settings-card {
  width: 100%;
  max-width: 700px;
  border-radius: 20px !important;
  background: white !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.theme--dark .settings-card {
  background: #1C1C1E !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Tab content */
.tab-content {
  padding: 1rem 0;
  min-height: 300px;
}

/* Button container centralizado */
.button-container {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.action-button {
  min-width: 150px;
}

/* Responsive */
@media (max-width: 600px) {
  #settings-manager {
    padding: 0.5rem;
  }

  .settings-card {
    max-width: 100%;
    border-radius: 16px !important;
  }

  .settings-header {
    position: static;
    margin-bottom: 2rem;
  }

  .button-container {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
    margin: 0 !important;
  }
}
</style>